// A new component: SignatureProgressDisplay.tsx
import React from 'react';
import { useSignatureContext } from '../context/SignatureContext';
import {
    Box,
    Typography,
    LinearProgress,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Alert,
    Tooltip
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as PendingIcon,
    Warning as WarningIcon,
    AccountCircle as AccountIcon
} from '@mui/icons-material';

const SignatureProgressDisplay: React.FC = () => {
    const { signatureProgress, hasSignature } = useSignatureContext();
    const { collectedCount, requiredCount, owners, completedSigners } = signatureProgress;

    // Calculate progress percentage
    const progressPercent = requiredCount > 0
        ? Math.min(100, (collectedCount / requiredCount) * 100)
        : 0;

    if (!owners || owners.length === 0) {
        return (
            <Alert severity="info">
                Wallet owner information is loading...
            </Alert>
        );
    }

    return (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
                MultiSig Signature Progress
            </Typography>

            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">
                        {collectedCount} of {requiredCount} required signatures
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {Math.round(progressPercent)}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: progressPercent >= 100 ? 'success.main' : 'primary.main'
                        }
                    }}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Wallet Owners ({owners.length})
                </Typography>
                <List>
                    {owners.map((owner, index) => {
                        const hasSigned = hasSignature(owner);
                        return (
                            <ListItem
                                key={owner}
                                sx={{
                                    borderBottom: index < owners.length - 1 ? '1px solid rgba(0,0,0,0.08)' : 'none',
                                    backgroundColor: hasSigned ? 'rgba(76, 175, 80, 0.1)' : 'transparent'
                                }}
                            >
                                <ListItemIcon>
                                    {hasSigned ? (
                                        <CheckCircleIcon color="success" />
                                    ) : (
                                        <PendingIcon color="action" />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Tooltip title={owner}>
                                                <Typography>
                                                    {owner.substring(0, 6)}...{owner.substring(owner.length - 4)}
                                                </Typography>
                                            </Tooltip>
                                            {hasSigned && (
                                                <Chip
                                                    size="small"
                                                    label="Signed"
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{ ml: 1, height: 20 }}
                                                />
                                            )}
                                        </Box>
                                    }
                                    secondary={hasSigned ? 'Signature collected' : 'Awaiting signature'}
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </Box>

            {collectedCount >= requiredCount ? (
                <Alert
                    icon={<CheckCircleIcon fontSize="inherit" />}
                    severity="success"
                >
                    All required signatures have been collected. You can proceed with the transaction.
                </Alert>
            ) : (
                <Alert
                    icon={<WarningIcon fontSize="inherit" />}
                    severity="warning"
                >
                    Additional signatures are needed. Please switch your wallet to one of the remaining owners and collect their signature.
                </Alert>
            )}
        </Paper>
    );
};

export default SignatureProgressDisplay;